import { Collection } from 'firestorter';
import { DateFormat, timeStampToStr } from '../utils/date';
import { getFeeAmountStr } from '../utils/fee';

import {
  ContractType, ContractStatus, CheckoutExperience, isDiscountedFirstMonthByAmounts, isStandardMonthlyByAmounts,
  isFutureStartDateAtCheckout,
} from '../CoachTools/utils/userContract';
import { firestorePaths } from '../utils/firebasePaths';
import { formatCurrencyCents } from '../utils/formatters';
import { DURATION_UNIT } from '../utils/durationUnit';
import { generateTerms } from '../CoachTools/utils/userContractTerms';
import BaseDocument from './BaseDocument';

// All contracts that don't have a version set are considered to be version 1.
const CONTRACT_VERSION_UNSET = 1;

class UserContract extends BaseDocument {
  constructor(id, opts) {
    super(`${firestorePaths.USER_CONTRACT}/${id}`, opts);
  }

  get type() {
    return this.data.type || ContractType.USER;
  }

  get usageCount() {
    return this.data.usageCount || 0;
  }

  get name() {
    return this.data.name || '';
  }

  get version() {
    return this.data.version || CONTRACT_VERSION_UNSET;
  }

  get current() {
    return this.data.current === true;
  }

  get leadId() {
    return this.data.leadId;
  }

  // The contract this one supersedes; absent on amendments raised before the link existed.
  get originalContract() {
    return this.data.originalContract;
  }

  get coach() {
    return this.data.coach;
  }

  get coachName() {
    return this.data.coachName || '';
  }

  get insideSalesId() {
    return this.data.insideSalesId;
  }

  get productId() {
    return this.data.productId;
  }

  get productName() {
    return this.data.productName || '';
  }

  get price() {
    return this.data.price || {};
  }

  get priceId() {
    return this.price.priceId || '';
  }

  get monthlyPriceId() {
    return this.price.monthlyPriceId || '';
  }

  get totalPriceInCents() {
    return this.price.totalPriceInCents;
  }

  get totalPriceInCentsStr() {
    return formatCurrencyCents(this.totalPriceInCents, this.currency);
  }

  get currency() {
    return this.price.currency;
  }

  get minSubscriptionMonths() {
    return this.price.minSubscriptionMonths;
  }

  get recurringBillingMonths() {
    return this.price.recurringBillingMonths || 1;
  }

  get initialPaymentInCents() {
    return this.price.initialPaymentInCents;
  }

  get initialPaymentInCentsStr() {
    return formatCurrencyCents(this.initialPaymentInCents, this.currency);
  }

  get initialTerm() {
    return this.price.initialTerm;
  }

  /**
   * The unit `initialTerm` and `minSubscriptionMonths` are counted in. Only
   * challenge contracts can carry 'week'; everything else is months, as is any
   * contract created before week durations existed.
   */
  get durationUnit() {
    return this.price.durationUnit || DURATION_UNIT.MONTH;
  }

  get postCommitmentPriceInCents() {
    return this.price.postCommitmentPriceInCents;
  }

  get cancelAtPeriodEnd() {
    return !!this.price.cancelAtPeriodEnd;
  }

  get upfrontPaymentInCents() {
    return this.price.upfrontPaymentInCents || 0;
  }

  get upsellContract() {
    return this.data.upsellContract || {};
  }

  get upsellProductId() {
    return this.upsellContract.productId || '';
  }

  get upsellMonthlyPriceInCents() {
    return this.upsellContract.monthlyPriceInCents || 0;
  }

  get startDate() {
    return this.data.startDate;
  }

  get userEmail() {
    return this.data.userEmail;
  }

  get userFirstName() {
    return this.data.userFirstName || '';
  }

  get userLastName() {
    return this.data.userLastName || '';
  }

  get userName() {
    return this.data.userName;
  }

  get userId() {
    return this.data.userId;
  }

  /** The reusable (BASE) contract this one was created from, or '' for a contract that is not. */
  get baseContractId() {
    return this.data.baseContract || '';
  }

  get challengeId() {
    return this.data.challengeId;
  }

  /**
   * The Inside Sales category this contract is offered under, if any.
   * Mirrors the `category` field on Package — a challenge is offered through its BASE contract.
   * @returns {string|null} One of the CATEGORY_CONFIG keys, or null when unassigned.
   */
  get category() {
    return this.data.category || null;
  }

  setCategory(category) {
    return this.updateFields({ category });
  }

  get fees() {
    return this.data.fees || {};
  }

  get baseFee() {
    return this.fees.baseFee || 0;
  }

  get baseFeeStr() {
    return getFeeAmountStr(this.totalPriceInCents, this.currency, this.baseFee);
  }

  get acceptUntil() {
    return this.data.acceptUntil;
  }

  get insideSalesFee() {
    return this.fees.insideSalesFee || 0;
  }

  get insideSalesFeeStr() {
    return getFeeAmountStr(this.totalPriceInCents, this.currency, this.insideSalesFee);
  }

  get assistantCoachFee() {
    return this.fees.assistantCoachFee || 0;
  }

  get assistantCoachFeeStr() {
    return getFeeAmountStr(this.totalPriceInCents, this.currency, this.assistantCoachFee);
  }

  get challengeFee() {
    return this.fees.challengeFee || 0;
  }

  get challengeFeeStr() {
    return getFeeAmountStr(this.totalPriceInCents, this.currency, this.challengeFee);
  }

  get aiFriendFee() {
    return this.fees.aiFriendFee || 0;
  }

  get aiFriendFeeStr() {
    return getFeeAmountStr(this.totalPriceInCents, this.currency, this.aiFriendFee);
  }

  get status() {
    return this.data.status;
  }

  get createdAt() {
    return this.data.createdAt;
  }

  get createdAtStr() {
    return timeStampToStr(this.createdAt, DateFormat.DATE_FORMAT_SLASH_WITH_TIME_FORMAT);
  }

  get updatedAt() {
    return this.data.updatedAt;
  }

  get updatedAtStr() {
    return timeStampToStr(this.updatedAt, DateFormat.DATE_FORMAT_SLASH_WITH_TIME_FORMAT);
  }

  get coachConditions() {
    return this.data.coachConditions;
  }

  get terms() {
    return this.data.terms || [];
  }

  get packageId() {
    return this.data.packageId || '';
  }

  get packageName() {
    return this.data.packageName || '';
  }

  get sellerId() {
    return this.data.sellerId || '';
  }

  get phoneNumber() {
    return this.data.phoneNumber || '';
  }

  get isAmendment() {
    return !!this.data.isAmendment;
  }

  // This is the id of the primary contract that this contract is a secondary contract of.
  get primaryContractId() {
    return this.data.primaryContractId || '';
  }

  // This is the id of the secondary contract that this contract is a primary contract of.
  get secondaryContractId() {
    return this.data.secondaryContractId || '';
  }

  /*
    The shape helpers coerce, because the contract modal holds these as strings from number inputs.
    This side reads Firestore, where the server is the authority and compares strictly — initialTerm
    against 1 and initialPaymentInCents with a typeof check. Asserting the stored types here keeps
    the coercion from making web the more permissive of the two, which is how a contract gets routed
    to hosted checkout and then refused with a 403.
  */
  get hasServerComparablePriceTypes() {
    return typeof this.initialPaymentInCents === 'number' && typeof this.initialTerm === 'number';
  }

  get isMonthlyRecurringContract() {
    return this.initialTerm === 1
    && this.initialPaymentInCents === this.totalPriceInCents
    && !this.upfrontPaymentInCents;
  }

  /*
    Deliberately narrower than isMonthlyRecurringContract, which also gates the shipped
    AI-Friend hosted-checkout flow and must keep accepting post-commitment contracts.
    A Stripe Hosted Checkout session can only carry a single recurring price, so a
    post-commitment rate change has nowhere to live in it.

    This must stay an exact mirror of isStandardMonthlyRecurringContract in the server's
    hostedCheckoutEligibility.ts: anything this accepts and the server rejects is refused with a
    403 at the payment step, which the onboarding page recovers from by falling back to Elements —
    at the cost of a wasted round-trip, a Sentry error, and the coach's choice silently not
    applied. Hence the typeof guard — the price fields are unvalidated Firestore reads, and on a
    legacy document missing both amounts `undefined === undefined` would otherwise pass here while
    the server refused.
  */
  get isStandardMonthlyRecurringContract() {
    return this.hasServerComparablePriceTypes
      && isStandardMonthlyByAmounts({
        initialTerm: this.initialTerm,
        firstPaymentAmount: this.initialPaymentInCents,
        monthlyAmount: this.totalPriceInCents,
        upfrontAmount: this.upfrontPaymentInCents,
        postCommitmentAmount: this.postCommitmentPriceInCents,
      });
  }

  get isDiscountedFirstMonthContract() {
    return this.hasServerComparablePriceTypes && isDiscountedFirstMonthByAmounts({
      initialTerm: this.initialTerm,
      firstPaymentAmount: this.initialPaymentInCents,
      monthlyAmount: this.totalPriceInCents,
      upfrontAmount: this.upfrontPaymentInCents,
      postCommitmentAmount: this.postCommitmentPriceInCents,
    });
  }

  /*
    Read at checkout, in the customer's browser, where the coach's timezone is unknown — so this
    uses the runtime rule (UTC plus a day of slack) that the server also applies, not the local-day
    rule `contractHasFutureStartDate` applies when the configurator authors the contract.
  */
  get hasFutureStartDate() {
    return isFutureStartDateAtCheckout(this.data.startDate);
  }

  get checkoutExperience() {
    return this.data.checkoutExperience || CheckoutExperience.AVIDA_MANAGED;
  }

  /*
    Read off the stored value rather than the checkoutExperience getter: a challenge is sold on Stripe Hosted
    Checkout unless Avida-managed was picked for it, and the getter's Avida-managed default would read every
    challenge saved without a choice as that pick.
  */
  get isAvidaManagedCheckoutPicked() {
    return this.data.checkoutExperience === CheckoutExperience.AVIDA_MANAGED;
  }

  /*
    An explicit zero: a contract with no price set is malformed, not free, so
    undefined must not satisfy this.
  */
  get isFree() {
    return this.totalPriceInCents === 0
    && !this.initialPaymentInCents
    && !this.upfrontPaymentInCents;
  }

  // Free contracts sent as personal links can skip the terms step entirely.
  get skipTerms() {
    return !!this.data.skipTerms;
  }

  // Whether clients make the commitment sticker after buying this challenge. Read it off the BASE contract.
  get isStoryShareEnabled() {
    return !!this.data.isStoryShareEnabled;
  }

  async updateStatus(status) {
    await this.set({ status }, { merge: true });
  }

  async updateContractTermsFromContractData(updatedContract) {
    return this.updateFields({
      startDate: updatedContract.startDate.format(DateFormat.DEFAULT_DATE_FORMAT),
      updatedAt: new Date(),
      terms: generateTerms(updatedContract),
    });
  }

  /**
   * Get a contract by its id.
   * @param {string} id - The id of the contract to get.
   * @returns {Promise<UserContract>} The contract.
   */
  static async getContractById(id) {
    const contract = new UserContract(id);
    await contract.init();
    return contract;
  }

  /**
   * Builds the docs of a contracts collection. Unobserving thousands of ref-counted docs at once
   * overflows the stack, and docs in a collection get their updates from it anyway.
   */
  static createCollectionDocument({ id }, opts) {
    return new UserContract(id, { ...opts, disableObserverRefCount: true });
  }

  /**
   * Get user contracts that meet the given conditions.
   * If a condition value is undefined, it will not added as a condition
   *
   * @param {array} conditions - The array of conditions, each condition has property, comparator & value properties.
   * property - The database field name.
   * comparator - What comparison to perform ['==','in','<=','!=','>=',...].
   * value - The value to set for the database field.
   * @returns {Promise<Collection>} The collection of userContracts.
   */
  static async getContractsBy(conditions) {
    const contractsCollection = new Collection(() => firestorePaths.USER_CONTRACT, {
      createDocument: UserContract.createCollectionDocument,
      query: (ref) => {
        let query = ref;
        conditions.forEach(({ property, comparator, value }) => {
          if (value !== undefined) {
            query = query.where(property, comparator, value);
          }
        });
        return query;
      },
    });
    await contractsCollection.fetch();
    return contractsCollection;
  }

  static getContractsByCoach(coach) {
    return UserContract.getContractsBy([{
      property: 'coach',
      comparator: '==',
      value: coach,
    }]);
  }

  static getContractsBySellerId(sellerId) {
    return UserContract.getContractsBy([{
      property: 'sellerId',
      comparator: '==',
      value: sellerId,
    }]);
  }

  static async getSingleContractBy(conditions) {
    const contractsCollection = await UserContract.getContractsBy(conditions);
    if (contractsCollection.hasDocs) {
      return contractsCollection.docs[0];
    }

    return null;
  }

  static getCurrentContractByUserId(userId) {
    return UserContract.getSingleContractBy(
      [
        { property: 'userId', comparator: '==', value: userId },
        { property: 'current', comparator: '==', value: true },
      ],
    );
  }

  static async getContractsBySellerIdAndStatuses(sellerId, statuses, fromDate) {
    const query = [];

    if (sellerId) {
      query.push({
        property: 'sellerId',
        comparator: '==',
        value: sellerId,
      });
    }
    if (statuses) {
      query.push({
        property: 'status',
        comparator: 'in',
        value: statuses,
      });
    }
    if (fromDate) {
      query.push({
        property: 'startDate',
        comparator: '>=',
        value: fromDate,
      });
    }

    return UserContract.getContractsBy(query);
  }

  /*
    TODO: We cannot use getContractsBy because we have lost the flexibility that regular firebase queries offer (We'd
    have to also implement extra props like orderBy, limit, etc). We have to come up with a better solution.

    This will load the most recent contract for a lead. If the lead was eligible for an automated secondary contract,
    the loaded contract will be that one, as the secondary contract is created right after the primary contract.
  */
  static async getContractByLeadId(leadId) {
    const contractsCollection = new Collection(() => firestorePaths.USER_CONTRACT, {
      query: (ref) => ref
        .where('leadId', '==', leadId)
        .orderBy('createdAt', 'desc')
        .limit(1),
    });
    await contractsCollection.fetch();

    let doc = null;
    if (contractsCollection.hasDocs) {
      doc = new UserContract(contractsCollection.docs[0].id);
      await doc.init();
    }

    return doc;
  }

  static getActivatableContractsForLead(leadId) {
    return UserContract.getContractsBy([{
      property: 'leadId',
      comparator: '==',
      value: leadId,
    }, {
      property: 'status',
      comparator: 'in',
      value: [ContractStatus.PENDING, ContractStatus.IN_PROGRESS],
    }]);
  }

  static getContractsByLeadId(leadId) {
    return UserContract.getContractsBy([{
      property: 'leadId',
      comparator: '==',
      value: leadId,
    }]);
  }

  static async getContractsByUserId(userId) {
    return UserContract.getContractsBy([{
      property: 'userId',
      comparator: '==',
      value: userId,
    }]);
  }

  /**
   * One coach's contracts with a client. A client can hold contracts with more than one coach,
   * and every action behind a contract row — cancel, pause, void, retry, refund — acts on real
   * money, so a coach must never be shown another coach's.
   *
   * @param {string} userId The client.
   * @param {string} coachId The coach whose page is open.
   * @return {Promise<Collection>} The contracts collection.
   */
  static async getContractsByUserAndCoach(userId, coachId) {
    return UserContract.getContractsBy([{
      property: 'userId',
      comparator: '==',
      value: userId,
    }, {
      property: 'coach',
      comparator: '==',
      value: coachId,
    }]);
  }

  static async getReonboardingContractsCollectionForUser(userId) {
    const contractsCollection = new Collection(() => firestorePaths.USER_CONTRACT, {
      createDocument: UserContract.createCollectionDocument,
      query: (ref) => ref
        .where('userId', '==', userId)
        .where('status', 'in', [ContractStatus.IN_PROGRESS, ContractStatus.PENDING]),
    });
    await contractsCollection.fetch();
    return contractsCollection;
  }

  static async getBaseContracts(fromDate = '') {
    const contractsCollection = new Collection(() => firestorePaths.USER_CONTRACT, {
      createDocument: UserContract.createCollectionDocument,
      query: (ref) => {
        let query = ref.where('type', '==', ContractType.BASE);
        if (fromDate) {
          query = query.where('createdAt', '>=', new Date(fromDate));
        }
        return query;
      },
    });
    await contractsCollection.fetch();
    return contractsCollection;
  }

  static getBaseContractsByCoach(coachId, fromDate = '') {
    const conditions = [
      {
        property: 'coach',
        comparator: '==',
        value: coachId,
      },
      {
        property: 'type',
        comparator: '==',
        value: ContractType.BASE,
      },
    ];
    if (fromDate) {
      conditions.push({
        property: 'createdAt',
        comparator: '>=',
        value: new Date(fromDate),
      });
    }
    return UserContract.getContractsBy(conditions);
  }

  static async getBaseContractByPriceId(priceId) {
    const contractsCollection = new Collection(() => firestorePaths.USER_CONTRACT, {
      createDocument: UserContract.createCollectionDocument,
      query: (ref) => ref
        .where('price.priceId', '==', priceId)
        .where('type', '==', ContractType.BASE),
    });
    await contractsCollection.fetch();

    if (contractsCollection.hasDocs) {
      return contractsCollection.docs[0];
    }

    return null;
  }

  /**
   * Get the BASE (DPL) contract that defines a challenge.
   *
   * There is exactly one per challenge, and every participant's contract is
   * copied from it — so it is the only unambiguous source of the challenge's
   * name and start date. Participant copies drift: their start dates are
   * re-stamped on activation and routinely disagree with each other.
   *
   * @param {string} challengeId - The id of the challenge config.
   * @returns {Promise<UserContract|null>} The challenge's base contract.
   */
  static getBaseContractByChallengeId(challengeId) {
    return UserContract.getSingleContractBy([
      { property: 'challengeId', comparator: '==', value: challengeId },
      { property: 'type', comparator: '==', value: ContractType.BASE },
    ]);
  }

  /**
   * Get the coach's challenge contracts that are assigned to an Inside Sales category.
   *
   * A challenge is offered to leads through its BASE contract, so this is the contract-side
   * counterpart of Package.getCategorizedPackages.
   *
   * Needs the userContract composite index on coach + type + category, since asking for
   * `category != null` alongside the two equalities makes this an inequality query. Without that
   * index Firestore rejects it outright, so the index ships with this feature.
   *
   * Only `challengeId` is filtered in memory — Firestore cannot express "this field exists", and
   * the categorized set is at most one contract per category.
   *
   * @param {string} coachId The coach whose offerings to read.
   * @returns {Promise<Array<UserContract>>} The categorized challenge contracts.
   */
  static async getCategorizedChallengeContractsByCoach(coachId) {
    const contractsCollection = await UserContract.getContractsBy([
      { property: 'coach', comparator: '==', value: coachId },
      { property: 'type', comparator: '==', value: ContractType.BASE },
      { property: 'category', comparator: '!=', value: null },
    ]);
    return contractsCollection.docs.filter((contract) => !!contract.challengeId);
  }

  static addDoc = async (data) => {
    const userContractCollection = new Collection(firestorePaths.USER_CONTRACT);
    const newContractDoc = await userContractCollection.add(data);
    const userContractDoc = new UserContract(newContractDoc.id);
    await userContractDoc.init();
    return userContractDoc;
  };

  static createBaseContract = (data) => {
    const docData = {
      ...data,
      type: ContractType.BASE,
    };

    return UserContract.addDoc(docData);
  };
}

export default UserContract;
